import { imgSrcSet } from "../lib/media";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Check, Globe, Users, Wallet, Palette, Home, Utensils, Leaf, Gift, ImageIcon } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useConfig, symbolForCurrency, fmtAmount, formatMoney } from "../lib/format";
import { currencyForCountry } from "../lib/locale";
import SnowLayer from "../components/SnowLayer";
import { Modal } from "../components/Modal";

const CEIA_LEVELS = ["economica", "tradicional", "especial", "premium"];
const CEIA_LABEL = {
  economica: { pt: "Econômica", "pt-PT": "Económica", en: "Budget-friendly", es: "Económica", fr: "Économique", it: "Economica" },
  tradicional: { pt: "Tradicional", "pt-PT": "Tradicional", en: "Traditional", es: "Tradicional", fr: "Traditionnelle", it: "Tradizionale" },
  especial: { pt: "Especial", "pt-PT": "Especial", en: "Special", es: "Especial", fr: "Spéciale", it: "Speciale" },
  premium: { pt: "Premium", "pt-PT": "Premium", en: "Premium", es: "Premium", fr: "Premium", it: "Premium" },
};
const QUICK_NEEDS = ["decoration", "ceia", "table", "gifts"];
const NEED_LABEL = { decoration: "nav.decoration", ceia: "nav.ceia", table: "nav.table", gifts: "nav.gifts" };
const PRESETS = { BRL: [500, 1000, 2500, 5000], USD: [150, 300, 800, 1500], EUR: [150, 300, 800, 1500] };

export default function CreateChristmas() {
  const { t, lang, setLang, languages } = useI18n();
  const { user, setUser } = useAuth();
  const config = useConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const quick = params.get("quick") === "1";
  const editMode = params.get("edit") === "1";

  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("form"); // form | processing | result
  const [procMsg, setProcMsg] = useState(0);
  const [computed, setComputed] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [ans, setAns] = useState({
    language: lang, country: user?.country || "BR", guests: 8, audience: [],
    budget_total: null, budget_mode: "auto", budget_split: { decoration: 25, ceia: 37, gifts: 28, table: 10 },
    style: "tradicional", haves: [], ceia_intent: "maybe", ceia_style: "tradicional",
    dietary: ["none"], secret_santa: "maybe", needs: [], name: "", cover: "tradicional", year: 2026,
  });

  useEffect(() => { api.get(`/christmas/options?lang=${lang}`).then(({ data }) => setOpts(data)).catch(() => {}); }, [lang]);

  useEffect(() => {
    if (!editMode) return;
    api.get(`/christmas?lang=${lang}`).then(({ data }) => {
      const p = data?.plan;
      if (!p) return;
      setAns((a) => ({
        ...a,
        country: p.country ?? a.country,
        guests: p.guests ?? a.guests,
        audience: p.audience ?? a.audience,
        budget_total: p.budget_total ?? a.budget_total,
        budget_mode: p.budget_mode ?? a.budget_mode,
        budget_split: p.budget_split ?? a.budget_split,
        style: p.style ?? a.style,
        haves: p.haves ?? a.haves,
        ceia_intent: p.ceia_intent ?? a.ceia_intent,
        ceia_style: p.ceia_style ?? a.ceia_style,
        dietary: p.dietary ?? a.dietary,
        secret_santa: p.secret_santa ?? a.secret_santa,
        name: p.name ?? a.name,
        cover: p.cover ?? a.cover,
        year: p.year ?? a.year,
      }));
    }).catch(() => {});
  }, [editMode, lang]);

  const currency = currencyForCountry(config, ans.country);
  const symbol = symbolForCurrency(config, currency);
  const set = (patch) => { setDirty(true); setAns((a) => ({ ...a, ...patch })); };
  const toggle = (key, id, exclusiveIds = []) => { setDirty(true); return setAns((a) => {
    let arr = a[key] || [];
    if (exclusiveIds.includes(id)) return { ...a, [key]: arr.includes(id) ? [] : [id] };
    arr = arr.filter((x) => !exclusiveIds.includes(x));
    return { ...a, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
  }); };

  const goHome = () => {
    if (phase === "result" || !dirty) navigate("/app");
    else setShowLeave(true);
  };

  const steps = useMemo(() => {
    if (!opts) return [];
    const S = [];
    if (!quick) {
      S.push({ id: "language", icon: Globe, q: t("wiz.q_language"), body: (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="wiz-language">
          {languages.map((l) => (
            <ChoiceBtn key={l.code} active={ans.language === l.code} onClick={() => { set({ language: l.code }); setLang(l.code); }} testid={`wiz-lang-${l.code}`}>{l.label}</ChoiceBtn>
          ))}
        </div>
      ) });
    }
    S.push({ id: "country", icon: Globe, q: t("wiz.q_country"), sub: t("wiz.q_country_sub"), body: (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="wiz-country">
        {(config?.countries || []).map((c) => (
          <button key={c.code} data-testid={`wiz-country-${c.code}`} onClick={() => set({ country: c.code })}
            className={`rounded-xl border p-4 text-center transition ${ans.country === c.code ? "border-xmas-gold bg-xmas-gold/10" : "border-xmas-gold/20 hover:border-xmas-gold/50"}`}>
            <span className="text-2xl">{c.flag}</span>
            <p className="mt-1 text-sm text-xmas-snow">{c.name?.[lang] || c.code}</p>
            <p className="text-[11px] text-xmas-slate">{c.currency} · {c.symbol}</p>
          </button>
        ))}
      </div>
    ) });
    S.push({ id: "guests", icon: Users, q: t("wiz.q_guests"), body: (
      <div data-testid="wiz-guests">
        <div className="flex flex-wrap justify-center gap-3">
          {[2, 4, 6, 8, 10, 15, 20].map((g) => (
            <button key={g} onClick={() => set({ guests: g })} data-testid={`wiz-guests-${g}`}
              className={`h-14 w-14 rounded-full text-lg font-semibold transition ${ans.guests === g ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>{g}</button>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-center gap-2" data-testid="wiz-guests-custom">
          <span className="text-sm text-xmas-slate">{t("wiz.guests_other")}</span>
          <input type="number" min={1} max={500} inputMode="numeric" data-testid="wiz-guests-input"
            value={[2, 4, 6, 8, 10, 15, 20].includes(ans.guests) ? "" : ans.guests}
            placeholder={t("wiz.guests_ph")}
            onChange={(e) => { const v = parseInt(e.target.value, 10); if (e.target.value === "") return; if (v >= 1 && v <= 500) set({ guests: v }); }}
            className={`w-24 rounded-full border px-3 py-2 text-center text-sm outline-none focus:border-xmas-gold ${![2, 4, 6, 8, 10, 15, 20].includes(ans.guests) ? "border-xmas-gold bg-xmas-gold/15 text-xmas-snow" : "border-xmas-gold/20 bg-xmas-navy/50 text-xmas-snow"}`} />
          <span className="text-sm text-xmas-slate">{t("wiz.people")}</span>
        </div>
        {!quick && (
          <>
            <p className="mt-6 mb-2 text-sm text-xmas-slate">{t("wiz.q_audience")} <span className="text-xs">· {t("wiz.q_audience_sub")}</span></p>
            <div className="flex flex-wrap justify-center gap-2">
              {opts.audience.map((a) => (
                <Chip key={a.id} active={ans.audience.includes(a.id)} onClick={() => toggle("audience", a.id)} testid={`wiz-audience-${a.id}`}>{a.name}</Chip>
              ))}
            </div>
          </>
        )}
      </div>
    ) });
    const DEFAULT_PCT = { decoration: 25, ceia: 37, gifts: 28, table: 10 };
    const proportional = (keys) => {
      const base = keys.reduce((a, k) => a + DEFAULT_PCT[k], 0);
      const out = { decoration: 0, ceia: 0, gifts: 0, table: 0 };
      keys.forEach((k) => { out[k] = Math.round(DEFAULT_PCT[k] * 100 / base); });
      const top = keys.reduce((a, k) => (DEFAULT_PCT[k] > DEFAULT_PCT[a] ? k : a), keys[0]);
      out[top] += 100 - Object.values(out).reduce((a, b) => a + b, 0);
      return out;
    };
    const splitPanel = (keys) => {
      const totalPct = keys.reduce((a, k) => a + (ans.budget_split[k] || 0), 0);
      const allocated = Math.round((ans.budget_total || 0) * totalPct / 100);
      const over = allocated > (ans.budget_total || 0);
      return (
        <div className="mt-6">
          <p className="mb-2 text-sm text-xmas-slate">{t("wiz.q_split")}</p>
          <div className="flex justify-center gap-2">
            <Chip active={ans.budget_mode === "auto"} onClick={() => set({ budget_mode: "auto" })} testid="wiz-split-auto">{t("wiz.split_auto")}</Chip>
            <Chip active={ans.budget_mode === "manual"} onClick={() => set({ budget_mode: "manual", budget_split: keys.length < 4 ? proportional(keys) : ans.budget_split })} testid="wiz-split-manual">{t("wiz.split_manual")}</Chip>
          </div>
          {ans.budget_mode === "manual" && (
            <div className="mt-4 space-y-2" data-testid="wiz-split-manual-panel">
              {keys.map((k) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="w-24 text-left text-xs text-xmas-slate">{t(`split.${k}`)}</span>
                  <input type="range" min="0" max="100" value={ans.budget_split[k] || 0} onChange={(e) => set({ budget_split: { ...ans.budget_split, [k]: Number(e.target.value) } })} className="flex-1 accent-xmas-gold" data-testid={`wiz-split-range-${k}`} />
                  <span className="w-28 text-right text-xs text-xmas-gold">{ans.budget_split[k] || 0}% · {formatMoney(symbol, Math.round((ans.budget_total || 0) * (ans.budget_split[k] || 0) / 100))}</span>
                </div>
              ))}
              <p className={`text-right text-[11px] ${over ? "text-red-400" : "text-xmas-slate"}`}>Σ {formatMoney(symbol, allocated)}</p>
              {over && (
                <p className="rounded-lg border border-red-400/40 bg-red-400/10 px-3 py-2 text-center text-xs font-medium text-red-400" data-testid="wiz-budget-over-msg">
                  {t("wiz.budget_over")}
                </p>
              )}
            </div>
          )}
        </div>
      );
    };
    S.push({ id: "budget", icon: Wallet, q: t("wiz.q_budget"), body: (
      <div data-testid="wiz-budget">
        <div className="flex flex-wrap justify-center gap-3">
          {(PRESETS[currency] || PRESETS.EUR).map((v) => (
            <button key={v} onClick={() => set({ budget_total: v })} data-testid={`wiz-budget-${v}`}
              className={`rounded-full px-5 py-3 text-sm transition ${ans.budget_total === v ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>{formatMoney(symbol, v)}</button>
          ))}
        </div>
        <div className="mx-auto mt-4 flex max-w-xs flex-col items-center gap-1">
          <span className="text-xs text-xmas-slate" data-testid="wiz-budget-custom-label">{t("wiz.budget_custom")}</span>
          <div className="flex w-full items-center gap-2 rounded-full border border-xmas-gold/25 px-4 py-2">
            <span className="text-xmas-gold">{symbol}</span>
            <input type="text" inputMode="numeric" data-testid="wiz-budget-custom" value={ans.budget_total ? fmtAmount(currency, ans.budget_total) : ""} onChange={(e) => { const d = String(e.target.value).replace(/\D/g, ""); set({ budget_total: d ? Number(d) / 100 : null }); }}
              className="w-full bg-transparent text-center text-xmas-snow outline-none" placeholder="" />
          </div>
        </div>
        {!quick && ans.budget_total ? splitPanel(["decoration", "ceia", "gifts", "table"]) : null}
      </div>
    ) });
    S.push({ id: "style", icon: Palette, q: t("wiz.q_style"), body: (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="wiz-style">
        {opts.styles.map((s) => (
          <button key={s.id} data-testid={`wiz-style-${s.id}`} onClick={() => set({ style: s.id })}
            className={`group flex h-64 flex-col overflow-hidden rounded-xl border text-left transition ${ans.style === s.id ? "border-xmas-gold ring-2 ring-xmas-gold/40" : "border-xmas-gold/20 hover:border-xmas-gold/50"}`}>
            <div className="relative h-[70%] w-full">
              <img src={s.img} srcSet={imgSrcSet(s.img)} sizes="(max-width: 640px) 50vw, 300px" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              {ans.style === s.id && <div className="absolute right-2 top-2 rounded-full bg-xmas-gold p-1"><Check className="h-3 w-3 text-xmas-navy" /></div>}
            </div>
            <div className="flex h-[30%] flex-col justify-center bg-xmas-navycard px-4">
              <p className="text-base font-semibold text-xmas-snow">{s.name}</p>
              <p className="mt-0.5 line-clamp-1 text-xs text-xmas-slate">{s.desc}</p>
              <div className="mt-1.5 flex gap-1.5">{s.palette.map((c) => <span key={c} className="h-3 w-3 rounded-full" style={{ background: c }} />)}</div>
            </div>
          </button>
        ))}
      </div>
    ) });
    if (quick) {
      S.push({ id: "needs", icon: Sparkles, q: t("wiz.q_quick_needs"), body: (
        <div>
        <div className="flex flex-wrap justify-center gap-2" data-testid="wiz-needs">
          {QUICK_NEEDS.map((n) => (
            <Chip key={n} active={ans.needs.includes(n)} onClick={() => toggle("needs", n)} testid={`wiz-need-${n}`}>{t(NEED_LABEL[n])}</Chip>
          ))}
        </div>
        {ans.budget_total && ans.needs.length >= 2 ? splitPanel(["decoration", "ceia", "gifts", "table"].filter((k) => ans.needs.includes(k))) : null}
        </div>
      ) });
    } else {
      S.push({ id: "haves", icon: Home, q: t("wiz.q_haves"), sub: t("wiz.q_haves_sub"), body: (
        <div className="flex flex-wrap justify-center gap-2" data-testid="wiz-haves">
          {opts.haves.map((h) => (
            <Chip key={h.id} active={ans.haves.includes(h.id)} onClick={() => toggle("haves", h.id, ["nothing", "later"])} testid={`wiz-have-${h.id}`}>{h.name}</Chip>
          ))}
        </div>
      ) });
      S.push({ id: "ceia", icon: Utensils, q: t("wiz.q_ceia"), body: (
        <div data-testid="wiz-ceia">
          <div className="flex flex-wrap justify-center gap-2">
            {opts.ceia_intent.map((c) => (
              <Chip key={c.id} active={ans.ceia_intent === c.id} onClick={() => set({ ceia_intent: c.id })} testid={`wiz-ceia-${c.id}`}>{c.name}</Chip>
            ))}
          </div>
          {ans.ceia_intent === "yes" && (
            <>
              <p className="mt-6 mb-2 text-sm text-xmas-slate">{t("wiz.q_ceia_level")}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {CEIA_LEVELS.map((lv) => (
                  <Chip key={lv} active={ans.ceia_style === lv} onClick={() => set({ ceia_style: lv })} testid={`wiz-ceia-level-${lv}`}>{CEIA_LABEL[lv][lang]}</Chip>
                ))}
              </div>
            </>
          )}
        </div>
      ) });
      S.push({ id: "dietary", icon: Leaf, q: t("wiz.q_dietary"), body: (
        <div className="flex flex-wrap justify-center gap-2" data-testid="wiz-dietary">
          {opts.dietary.map((d) => (
            <Chip key={d.id} active={ans.dietary.includes(d.id)} onClick={() => toggle("dietary", d.id, ["none"])} testid={`wiz-dietary-${d.id}`}>{d.name}</Chip>
          ))}
        </div>
      ) });
      S.push({ id: "secret_santa", icon: Gift, q: t("wiz.q_secretsanta"), body: (
        <div className="flex flex-wrap justify-center gap-2" data-testid="wiz-ss">
          {opts.ceia_intent.map((c) => (
            <Chip key={c.id} active={ans.secret_santa === c.id} onClick={() => set({ secret_santa: c.id })} testid={`wiz-ss-${c.id}`}>{c.name}</Chip>
          ))}
        </div>
      ) });
      S.push({ id: "name", icon: ImageIcon, q: t("wiz.q_name"), sub: t("wiz.q_name_sub"), body: (
        <div data-testid="wiz-name">
          <input value={ans.name} onChange={(e) => set({ name: e.target.value })} data-testid="wiz-name-input"
            className="mx-auto mb-6 block w-full max-w-sm rounded-full border border-xmas-gold/25 bg-transparent px-5 py-3 text-center text-xmas-snow outline-none focus:border-xmas-gold" placeholder={t("wiz.name_ph")} />
          <p className="mb-2 text-sm text-xmas-slate">{t("wiz.q_cover")}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {opts.covers.map((c) => (
              <button key={c.id} data-testid={`wiz-cover-${c.id}`} onClick={() => set({ cover: c.id })}
                className={`group relative overflow-hidden rounded-xl border transition ${ans.cover === c.id ? "border-xmas-gold ring-2 ring-xmas-gold/40" : "border-xmas-gold/20 hover:border-xmas-gold/50"}`}>
                <img src={c.img} srcSet={imgSrcSet(c.img)} sizes="(max-width: 640px) 50vw, 220px" alt={c.name} loading="lazy" decoding="async" className="h-20 w-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-[10px] text-xmas-snow">{c.name}</span>
                {ans.cover === c.id && <div className="absolute right-1.5 top-1.5 rounded-full bg-xmas-gold p-0.5"><Check className="h-3 w-3 text-xmas-navy" /></div>}
              </button>
            ))}
          </div>
        </div>
      ) });
    }
    return S;
  }, [opts, quick, ans, config, lang]); // eslint-disable-line

  const cur = steps[step];
  const total = steps.length;

  const submit = async () => {
    setPhase("processing");
    const msgs = [0, 1, 2, 3, 4];
    let i = 0;
    const iv = setInterval(() => { i++; setProcMsg(i % msgs.length); }, 700);
    try {
      const payload = { ...ans, mode: quick ? "quick" : "full" };
      if (quick && ans.budget_mode === "manual" && ans.needs.length) {
        payload.budget_split = Object.fromEntries(Object.keys(ans.budget_split).map((k) => [k, ans.needs.includes(k) ? ans.budget_split[k] : 0]));
      }
      const { data } = await api.post(`/christmas?lang=${lang}`, payload);
      setComputed(data.computed);
      setUser({ ...user, active_plan_id: data.plan.plan_id, country: data.plan.country, currency: data.plan.currency,
        onboarding: { country: data.plan.country, guests: data.plan.guests, budget: data.plan.budget_total, style: data.plan.style } });
      setTimeout(() => { clearInterval(iv); setPhase("result"); }, 2600);
    } catch (e) {
      clearInterval(iv);
      setPhase("form");
      const status = e?.response?.status;
      if (status === 401) { toast.error(t("wiz.err_session")); navigate("/login", { replace: true }); return; }
      toast.error(t("wiz.err_generic", { msg: e?.response?.data?.detail ? formatApiError(e.response.data.detail) : (e?.message || `HTTP ${status || "?"}`) }), { duration: 8000 });
    }
  };

  if (!opts) return <div className="flex min-h-screen items-center justify-center bg-xmas-navy text-xmas-slate">…</div>;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-xmas-navy" data-testid="create-christmas-page">
      <SnowLayer count={22} />
      <div className="relative z-10 w-full max-w-2xl px-4 py-10">
        {phase === "form" && cur && (
          <>
            <button onClick={goHome} data-testid="wiz-home" className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-xmas-gold/25 bg-xmas-navycard/60 px-3.5 py-2 text-sm text-xmas-champagne transition hover:border-xmas-gold/60 hover:text-xmas-gold">
              <ArrowLeft className="h-4 w-4" /> <Home className="h-3.5 w-3.5" /> {t("nav.back_home")}
            </button>
            <div className="mb-2 flex items-center justify-between text-xs text-xmas-slate">
              <span>{quick && <span className="mr-2 rounded-full bg-xmas-red/30 px-2 py-0.5 text-xmas-champagne">⚡ {t("wiz.quick_badge")}</span>}{t("wiz.step", { n: step + 1, total })}</span>
            </div>
            <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-xmas-navysurface">
              <div className="h-full rounded-full bg-gradient-to-r from-xmas-pine to-xmas-gold transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} data-testid="wiz-progress" />
            </div>
            <div className="glass rounded-3xl p-6 text-center sm:p-8">
              {cur.icon && <cur.icon className="mx-auto mb-3 h-7 w-7 text-xmas-gold" />}
              <h2 className="mb-1 font-display text-2xl text-xmas-snow">{cur.q}</h2>
              {cur.sub && <p className="mb-6 text-sm text-xmas-slate">{cur.sub}</p>}
              <div className={cur.sub ? "" : "mt-6"}>{cur.body}</div>
              <div className="mt-9 flex items-center justify-between">
                {step > 0 ? (
                  <button onClick={() => setStep(step - 1)} data-testid="wiz-back" className="inline-flex items-center gap-1 text-sm text-xmas-slate hover:text-xmas-snow"><ArrowLeft className="h-4 w-4" />{t("wiz.back")}</button>
                ) : <button onClick={goHome} className="text-sm text-xmas-slate hover:text-xmas-snow">{t("wiz.skip")}</button>}
                {step < total - 1 ? (
                  <button onClick={() => setStep(step + 1)} data-testid="wiz-next" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">{t("wiz.next")} <ArrowRight className="h-4 w-4" /></button>
                ) : (
                  <button onClick={submit} data-testid="wiz-submit" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider"><Sparkles className="h-4 w-4" /> {t("create.cta_title")}</button>
                )}
              </div>
            </div>
          </>
        )}

        {phase === "processing" && (
          <div className="glass rounded-3xl p-10 text-center" data-testid="wiz-processing">
            <div className="mx-auto mb-6 h-14 w-14 animate-spin rounded-full border-2 border-xmas-gold/30 border-t-xmas-gold" />
            <h2 className="mb-2 font-display text-2xl text-xmas-gold">✨ {t("wiz.processing")}</h2>
            <p className="text-sm text-xmas-champagne">{t(`wiz.p${procMsg + 1}`)}</p>
          </div>
        )}

        {phase === "result" && computed && (
          <div className="glass rounded-3xl p-6 text-center sm:p-8" data-testid="wiz-result">
            <p className="text-xs uppercase tracking-widest text-xmas-gold">🎄 {t("wiz.ready")}</p>
            <h2 className="mb-5 mt-1 font-display text-3xl text-xmas-snow">{ans.name || t("wiz.summary")}</h2>
            <div className="mx-auto mb-6 grid max-w-sm grid-cols-2 gap-3 text-left text-sm">
              <SummRow label={t("plan.country")} value={config?.countries?.find((c) => c.code === ans.country)?.name?.[lang]} />
              <SummRow label={t("plan.guests")} value={`${ans.guests} ${t("wiz.people")}`} />
              <SummRow label={t("plan.style")} value={opts.styles.find((s) => s.id === ans.style)?.name} />
              <SummRow label={t("budget.total")} value={ans.budget_total ? formatMoney(computed.symbol, ans.budget_total) : "—"} />
              <SummRow label={t("nav.ceia")} value={ans.ceia_intent === "yes" ? CEIA_LABEL[ans.ceia_style][lang] : t("wiz.no")} />
              <SummRow label={t("nav.secretsanta")} value={ans.secret_santa === "yes" ? t("wiz.yes") : t("wiz.no")} />
            </div>
            <button onClick={() => navigate("/app")} data-testid="wiz-finish" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-bold uppercase tracking-wider">{t("wiz.finish")} <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <Modal open={showLeave} onClose={() => setShowLeave(false)} title={t("unsaved.title")} testid="wiz-unsaved-modal">
        <div className="space-y-3">
          <button onClick={() => setShowLeave(false)} data-testid="wiz-keep-editing" className="btn-gold w-full rounded-full py-3 text-sm font-bold uppercase tracking-wider">{t("unsaved.continue")}</button>
          <button onClick={() => navigate("/app")} data-testid="wiz-leave" className="w-full rounded-full border border-xmas-gold/25 py-3 text-sm text-xmas-slate transition hover:text-xmas-snow">{t("unsaved.leave")}</button>
        </div>
      </Modal>
    </div>
  );
}

function ChoiceBtn({ active, onClick, children, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-xl border py-3 text-sm transition ${active ? "border-xmas-gold bg-xmas-gold/10 text-xmas-gold" : "border-xmas-gold/20 text-xmas-snow hover:border-xmas-gold/50"}`}>{children}</button>;
}
function Chip({ active, onClick, children, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-full border px-4 py-2 text-sm transition ${active ? "border-xmas-gold bg-xmas-gold/15 text-xmas-gold" : "border-xmas-gold/20 text-xmas-slate hover:border-xmas-gold/50 hover:text-xmas-snow"}`}>{children}</button>;
}
function SummRow({ label, value }) {
  return (
    <div className="rounded-xl border border-xmas-gold/15 bg-white/5 p-3">
      <p className="text-[10px] uppercase tracking-wider text-xmas-slate">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-xmas-snow">{value || "—"}</p>
    </div>
  );
}
